import Loading from '@/assets/lottie/loading.json';
import OnboardingOverlay from '@/components/Onboarding';
import { useAuth } from '@/contexts/AuthContext';
import { shouldHideShopifyCallback } from '@/contexts/AuthContext/shopifyOnboardingResume';
import { useOnboarding } from '@/contexts/OnboardingContext';
import Lottie from 'lottie-react';
import { useEffect, useState } from 'react';
import { useTranslation } from '@client-app/i18n';
import { logSentryEvent } from '@/lib/sentry';
import { useSearchParams } from 'react-router-dom';

const LOADING_LOTTIE_STYLE = { width: 132, height: 100 };

type OnboardingBootstrapState = 'loading' | 'ready' | 'error';

function resolveOnboardingBootstrapState({
    hasEntered, isLoadingProfile, isLoadingWorkspaces, isLoadingInvitations,
    hasUser, hasProfile, hasWorkspaceError,
}: {
    hasEntered: boolean;
    isLoadingProfile: boolean;
    isLoadingWorkspaces: boolean;
    isLoadingInvitations: boolean;
    hasUser: boolean;
    hasProfile: boolean;
    hasWorkspaceError: boolean;
}): OnboardingBootstrapState {
    if (hasEntered) return 'ready';
    if (isLoadingProfile) return 'loading';
    if (!hasUser || !hasProfile) return 'error';
    if (isLoadingWorkspaces) return 'loading';
    if (hasWorkspaceError) return 'error';
    return isLoadingInvitations ? 'loading' : 'ready';
}

const OnboardingPage = () => {
    const {
        user, profile, isLoadingProfile, isLoadingWorkspaces, workspacesError,
        refreshWorkspaces, workspaceMembers: { isLoadingInvitations, refreshInvitations },
    } = useAuth();
    const { t } = useTranslation();
    const [enteredUserId, setEnteredUserId] = useState<string | null>(null);
    const [invitationCheck, setInvitationCheck] = useState<{
        userId: string; status: 'ready' | 'error';
    } | null>(null);
    const [invitationRetry, setInvitationRetry] = useState(0);
    const { onboardingData } = useOnboarding();
    const [searchParams] = useSearchParams();
    const shouldHideCallback = shouldHideShopifyCallback({ currentSearch: window.location.search });
    const inviteToken = searchParams.get('invite_token')?.trim() || '';
    const currentStep = onboardingData.currentStep ?? 0;
    const isConnectFlowActive = Boolean(
        onboardingData.workspaceId ||
        onboardingData.pendingConnectionRequest ||
        onboardingData.storeConnectionPlatform ||
        currentStep > 0
    );
    const shouldShowInvitationLoading = Boolean(inviteToken) && isLoadingInvitations && !isConnectFlowActive;

    // A saved step alone is a draft: it must not create a workspace before
    // invitation verification. An existing workspace can resume immediately.
    const canResumeExistingWorkspace = Boolean(onboardingData.workspaceId);
    const shouldCheckInvitations = !inviteToken;
    const currentInvitationCheck = invitationCheck?.userId === user?.id ? invitationCheck : null;
    const hasInvitationError = shouldCheckInvitations && currentInvitationCheck?.status === 'error';
    const hasProfile = Boolean(profile);

    // Welcome waits for this check. Resumed steps can stay visible while the
    // check runs, but cached invitations cannot replace them before it succeeds.
    useEffect(() => {
        if (!user?.id || isLoadingProfile || !hasProfile || !shouldCheckInvitations || shouldHideCallback) return;
        let cancelled = false;
        setInvitationCheck(null);
        const checkInvitations = async () => {
            try {
                await refreshInvitations({ throwOnError: true });
                if (!cancelled) setInvitationCheck({ userId: user.id, status: 'ready' });
            } catch {
                if (!cancelled) setInvitationCheck({ userId: user.id, status: 'error' });
            }
        };
        void checkInvitations();
        return () => { cancelled = true; };
    }, [user?.id, isLoadingProfile, hasProfile, shouldCheckInvitations, shouldHideCallback, refreshInvitations, invitationRetry]);

    // Initial data gates the first form mount. Background refreshes must not
    // unmount an active onboarding flow or discard its local input state.
    const bootstrapState = resolveOnboardingBootstrapState({
        hasEntered: Boolean(user?.id && enteredUserId === user.id),
        isLoadingProfile,
        isLoadingWorkspaces,
        isLoadingInvitations: !canResumeExistingWorkspace && (
            isLoadingInvitations || (shouldCheckInvitations && !currentInvitationCheck)
        ),
        hasUser: Boolean(user?.id),
        hasProfile: Boolean(profile),
        hasWorkspaceError: Boolean(workspacesError) || (!canResumeExistingWorkspace && Boolean(hasInvitationError)),
    });

    useEffect(() => {
        if (bootstrapState === 'ready' && user?.id) {
            setEnteredUserId(user.id);
        } else if (!user?.id) {
            setEnteredUserId(null);
        }
    }, [bootstrapState, user?.id]);

    useEffect(() => {
        if (shouldHideCallback) return;
        logSentryEvent('onboarding.bootstrap.state', {
            area: 'onboarding', route_kind: 'onboarding', state: bootstrapState,
            has_user: Boolean(user?.id), has_workspace_error: Boolean(workspacesError),
        });
    }, [bootstrapState, shouldHideCallback, user?.id, workspacesError]);

    if (shouldHideCallback) {
        return <div className="h-screen w-full bg-[#F8F9FB]" aria-hidden="true" />;
    }

    return (
        <div className="w-full h-screen bg-[#F8F9FB] flex items-center justify-center">
            {bootstrapState === 'error' ? (
                <div role="alert" className="flex flex-col items-center gap-4 px-6 text-center">
                    <p>{t('website.onboarding.common.loadFailed')}</p>
                    <button
                        className="rounded-xl border border-gray-200 bg-white px-4 py-2"
                        onClick={() => {
                            if (!user || !profile) {
                                window.location.reload();
                                return;
                            }
                            if (hasInvitationError) {
                                setInvitationCheck(null);
                                setInvitationRetry((attempt) => attempt + 1);
                                return;
                            }
                            return refreshWorkspaces({ skipEmptyRedirect: true });
                        }}
                    >
                        {t('common.retry')}
                    </button>
                </div>
            ) : bootstrapState === 'loading' || shouldShowInvitationLoading ? (
                <div
                    style={{
                        zIndex: 9999,
                        position: 'absolute',
                        background: '#fff',
                        top: 0, left: 0, right: 0, bottom: 0, display: 'flex', justifyContent: 'center', alignItems: 'center'
                    }}
                >
                    <Lottie animationData={Loading} style={LOADING_LOTTIE_STYLE} />
                </div>
            ) : null}
            {/* 这个页面主要作为 OnboardingOverlay 的背景载体 */}
            {bootstrapState === 'ready' ? (
                <OnboardingOverlay invitationsVerified={currentInvitationCheck?.status === 'ready'} />
            ) : null}
        </div>
    );
};

export default OnboardingPage;
